import { FC, useEffect, useRef, memo } from 'react';
import Quill from 'quill';
import QuillTableBetter from 'quill-table-better';
import 'quill/dist/quill.snow.css';
import 'quill-table-better/dist/quill-table-better.css';

import FormatBoldOutlinedIcon from '@mui/icons-material/FormatBoldOutlined';
import FormatItalicOutlinedIcon from '@mui/icons-material/FormatItalicOutlined';
import FormatQuoteOutlinedIcon from '@mui/icons-material/FormatQuoteOutlined';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import AddLinkOutlinedIcon from '@mui/icons-material/AddLinkOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import Box from '@mui/material/Box';
import { removeEmptyBrTags } from 'helpers/functions';
import { languageMapping } from './const';
import i18n from 'i18n';

interface EditorType {
    value: string;
    onChange: (value: string) => void;
    title?: string;
    isEditable?: boolean;
    readOnly?: boolean;
    placeholder?: string;
    onCloseParentFullScreen?: () => void;
    onOpenFullScreen?: () => void;
    isFullScreen?: boolean;
    onEnter?: () => void;
    showToolbar?: boolean;
}

Quill.register(
    {
        'modules/table-better': QuillTableBetter,
    },
    true,
);

const Editor: FC<EditorType> = ({
    value,
    onChange,
    title,
    isEditable = false,
    readOnly = false,
    placeholder,
    onCloseParentFullScreen,
    isFullScreen = false,
    onOpenFullScreen,
    showToolbar = true,
}) => {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<Quill | null>(null);
    const isUpdatingFromProp = useRef(false);
    const lastEmittedValue = useRef<string>('');

    useEffect(() => {
        if (!editorRef.current || quillRef.current) {
            return;
        }

        const toolbarOptions = [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ header: 1 }, { header: 2 }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ script: 'sub' }, { script: 'super' }],
            [{ indent: '-1' }, { indent: '+1' }],
            ['table-better'],
        ];

        const quill = new Quill(editorRef.current, {
            theme: 'snow',
            readOnly: readOnly,
            placeholder: placeholder || '',
            modules: {
                toolbar: toolbarOptions,
                table: false,
                'table-better': {
                    language: languageMapping[i18n.resolvedLanguage ?? 'en_US'],
                    menus: ['column', 'row', 'merge', 'table', 'cell', 'wrap', 'copy', 'delete'],
                    toolbarTable: true,
                },
                keyboard: {
                    bindings: QuillTableBetter.keyboardBindings,
                },
            },
        });

        quillRef.current = quill;

        // Add custom tooltip to Quill container
        if (tooltipRef.current && !readOnly) {
            quill.addContainer(tooltipRef.current);
        }

        // Set initial value
        if (value) {
            quill.clipboard.dangerouslyPasteHTML(value);
        }

        // Handle text changes
        quill.on('text-change', () => {
            // Skip onChange if we're updating from props
            if (isUpdatingFromProp.current) {
                return;
            }
            const html = quill.root.innerHTML;
            lastEmittedValue.current = html;
            onChange(removeEmptyBrTags(html));
        });

        // Focus the editor
        quill.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update content when value changes externally
    useEffect(() => {
        if (!quillRef.current) {
            return;
        }

        const currentContent = quillRef.current.root.innerHTML;

        // Skip update if the value is the same as what we just emitted
        if (value === lastEmittedValue.current) {
            return;
        }

        const cleanedCurrent = removeEmptyBrTags(currentContent);
        const cleanedValue = removeEmptyBrTags(value);

        // Only update if the content is actually different
        if (cleanedValue !== cleanedCurrent && value !== currentContent) {
            isUpdatingFromProp.current = true;
            const selection = quillRef.current.getSelection();

            // Clear all content first, then paste at position 0
            const length = quillRef.current.getLength();
            quillRef.current.deleteText(0, length);
            quillRef.current.clipboard.dangerouslyPasteHTML(0, value);

            if (selection) {
                quillRef.current.setSelection(selection);
            }
            // Reset flag after a brief delay to ensure all events are processed
            const timeoutId = setTimeout(() => {
                isUpdatingFromProp.current = false;
            }, 0);

            return () => clearTimeout(timeoutId);
        }
    }, [value]);

    // Update readOnly state
    useEffect(() => {
        if (quillRef.current) {
            quillRef.current.enable(!readOnly);
        }
    }, [readOnly]);

    useEffect(() => {
        const quill = quillRef.current;
        const tooltip = tooltipRef.current;
        if (!quill) {
            return;
        }

        const selectionChange = function (range: { index: number; length: number } | null) {
            if (!tooltip) {
                return;
            }

            // Hide tooltip if no range or range has no selection
            if (!range || range.length === 0) {
                tooltip.style.display = 'none';
                return;
            }

            // Show tooltip for selected text
            const rangeBounds = quill.getBounds(range.index, range.length);
            if (!rangeBounds) {
                return;
            }
            tooltip.style.display = 'flex';

            // Calculate left position
            const tooltipWidth = tooltip.offsetWidth;
            let leftPosition = rangeBounds.left + rangeBounds.width / 2 - tooltipWidth / 2;
            if (leftPosition < 0) {
                leftPosition = 0;
                tooltip.classList.add('left-0-tooltip');
            } else {
                tooltip.classList.remove('left-0-tooltip');
            }
            tooltip.style.left = `${leftPosition}px`;
            tooltip.style.top = `${rangeBounds.bottom + 10}px`;
        };

        const textChange = function () {
            // Hide tooltip only if there's no selection after text change
            const range = quill?.getSelection();
            if (!range || range.length === 0) {
                if (tooltip) {
                    tooltip.style.display = 'none';
                }
            }
        };

        if (quill && !readOnly) {
            quill.on('selection-change', selectionChange);
            quill.on('text-change', textChange);
        }
        return () => {
            quill?.off('selection-change', selectionChange);
            quill?.off('text-change', textChange);
            if (tooltip) {
                tooltip.style.display = 'none';
            }
        };
    }, [readOnly]);

    const checkFormat = (name: string) => {
        const format = quillRef.current?.getFormat();
        const isExists = format?.[name];

        return isExists;
    };
    const onBold = () => {
        if (!quillRef.current) {
            return;
        }
        const isBold = checkFormat('bold');
        quillRef.current.format('bold', !isBold);
    };
    const onItalic = () => {
        if (!quillRef.current) {
            return;
        }
        const isItalic = checkFormat('italic');
        quillRef.current.format('italic', !isItalic);
    };
    const onLink = () => {
        if (!quillRef.current) {
            return;
        }
        const value = prompt('Enter link URL');
        quillRef.current.format('link', value);
    };
    const onBlockquote = () => {
        if (!quillRef.current) {
            return;
        }
        const isExists = checkFormat('blockquote');
        quillRef.current.format('blockquote', !isExists);
    };

    const onHeader1 = () => {
        if (!quillRef.current) {
            return;
        }
        quillRef.current.format('header', 1);
    };

    const onHeader2 = () => {
        if (!quillRef.current) {
            return;
        }
        quillRef.current.format('header', 2);
    };

    return (
        <Box
            className={`quill-editor-custom ${!showToolbar ? 'hide-toolbar' : 'show-toolbar'}`}
            sx={{
                height: '340px',
                '&  button.ql-table-better': {
                    '> .ql-table-select-container': {
                        zIndex: 10,
                        position: 'fixed',
                    },
                },
            }}
        >
            <Box id="tooltip-controls" ref={tooltipRef}>
                <button id="bold-button" onClick={onBold}>
                    <FormatBoldOutlinedIcon />
                </button>
                <button id="italic-button" onClick={onItalic}>
                    <FormatItalicOutlinedIcon />
                </button>
                <button id="link-button" onClick={onLink}>
                    <AddLinkOutlinedIcon />
                </button>
                <button id="header-1-button" onClick={onHeader1}>
                    H<sub>1</sub>
                </button>
                <button id="header-2-button" onClick={onHeader2}>
                    H<sub>2</sub>
                </button>
                <button id="blockquote-button" onClick={onBlockquote}>
                    <FormatQuoteOutlinedIcon />
                </button>
            </Box>
            {title && (
                <Box className="editor-title">
                    <Box className="editor-title-value">{title}</Box>
                    {isEditable && (
                        <Box className="actions">
                            {isFullScreen ? (
                                <button onClick={onCloseParentFullScreen}>
                                    <CloseFullscreenIcon />
                                </button>
                            ) : (
                                <button onClick={onOpenFullScreen}>
                                    <OpenInFullIcon />
                                </button>
                            )}
                        </Box>
                    )}
                </Box>
            )}
            <Box ref={editorRef} />
        </Box>
    );
};

export default memo(Editor);
